import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from '../../context/TranslationContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { RefreshCw, Activity, HardDrive, Clock, Terminal } from 'lucide-react';

import { getApiBaseUrl } from '@/lib/apiBase';
const API_URL = getApiBaseUrl();

const REFRESH_INTERVAL_MS = 30000;
const HISTORY_MAX = 60;

function uptimeDisplay(seconds) {
  if (!seconds && seconds !== 0) return '—';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

function memColor(mb) {
  if (!mb) return '#9ca3af';
  if (mb < 200) return '#22c55e';
  if (mb < 400) return '#f59e0b';
  return '#ef4444';
}

// This page is deliberately read-only — no restart/clear-logs action. See
// backend/routes/server_monitor.py's module docstring: an HTTP-triggered
// process restart is a real blast-radius escalation on a box that also runs
// other unrelated PM2 apps, and this session found no evidence (no OOM
// kills, no unexpected restarts — see PROGRESS.md 2026-09-06) that one is
// needed. A real restart still requires SSH + `pm2 restart thachari`.
export const ServerMonitor = () => {
  const { token } = useAuth();
  const { t } = useTranslation();
  const headers = { Authorization: `Bearer ${token}` };

  const [health, setHealth] = useState(null);
  const [status, setStatus] = useState('unknown');
  const [history, setHistory] = useState([]);
  const [logs, setLogs] = useState({ out: [], error: [] });
  const [lastCheck, setLastCheck] = useState(null);
  const intervalRef = useRef(null);

  const fetchHealth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/health`, { timeout: 8000 });
      setHealth(data);
      setStatus('online');
      setHistory((prev) => [
        ...prev.slice(-HISTORY_MAX + 1),
        { time: new Date().toLocaleTimeString(), memory_mb: data.memory_mb, status: 'online' },
      ]);
    } catch (err) {
      setStatus('offline');
      setHistory((prev) => [
        ...prev.slice(-HISTORY_MAX + 1),
        { time: new Date().toLocaleTimeString(), memory_mb: 0, status: 'offline' },
      ]);
    } finally {
      setLastCheck(new Date());
    }
  }, []);

  const fetchLogs = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/admin/server/logs`, { headers, params: { lines: 60 } });
      setLogs({ out: data.out || [], error: data.error || [] });
    } catch (err) {
      // non-fatal — health card still works without logs
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    fetchHealth();
    fetchLogs();
    intervalRef.current = setInterval(() => {
      fetchHealth();
      fetchLogs();
    }, REFRESH_INTERVAL_MS);
    return () => clearInterval(intervalRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logLine = (line, i) => (
    <div
      key={i}
      className={
        /error|exception|traceback/i.test(line)
          ? 'text-red-400'
          : /warning/i.test(line)
          ? 'text-amber-400'
          : 'text-slate-300'
      }
      style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}
    >
      {line}
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-serif font-medium">{t('admin_server_monitor_title', 'Server Monitor')}</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {t('admin_server_monitor_subtitle', 'Last checked')}: {lastCheck?.toLocaleTimeString() || '—'}
            {' · '}{t('admin_server_monitor_autorefresh', 'auto-refreshes every 30s')}
          </p>
        </div>
        <Button variant="outline" onClick={() => { fetchHealth(); fetchLogs(); }}>
          <RefreshCw className="w-4 h-4 mr-2" />
          {t('admin_btn_refresh', 'Refresh')}
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-6 text-center">
            <div className="text-3xl mb-2">{status === 'online' ? '🟢' : '🔴'}</div>
            <p className="font-semibold text-lg">
              {status === 'online' ? t('admin_status_online', 'Online') : t('admin_status_offline', 'Offline')}
            </p>
            <p className="text-xs text-muted-foreground mt-1">{t('admin_server_api_status', 'API Status')}</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6 text-center">
            <HardDrive className="w-7 h-7 mx-auto mb-2 text-muted-foreground" />
            <p className="font-semibold text-lg" style={{ color: memColor(health?.memory_mb) }}>
              {health?.memory_mb ? `${health.memory_mb} MB` : '—'}
            </p>
            <p className="text-xs text-muted-foreground mt-1">{t('admin_server_memory', 'Memory Usage (peak RSS)')}</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6 text-center">
            <Clock className="w-7 h-7 mx-auto mb-2 text-muted-foreground" />
            <p className="font-semibold text-lg">{uptimeDisplay(health?.uptime_seconds)}</p>
            <p className="text-xs text-muted-foreground mt-1">{t('admin_server_uptime', 'Process Uptime')}</p>
          </CardContent>
        </Card>
      </div>

      {history.length > 1 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Activity className="w-4 h-4" />
              {t('admin_server_mem_history', 'Memory History')} ({history.length} {t('admin_server_checks', 'checks')})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-end gap-0.5 h-20">
              {history.map((point, i) => {
                const maxMem = 500;
                const height = point.status === 'offline' ? 80 : Math.max(4, (point.memory_mb / maxMem) * 80);
                return (
                  <div
                    key={i}
                    title={`${point.time}: ${point.status === 'offline' ? 'offline' : point.memory_mb + 'MB'}`}
                    className="flex-1 rounded-t-sm"
                    style={{
                      height: `${height}px`,
                      minWidth: 3,
                      background: point.status === 'offline' ? '#fecaca' : memColor(point.memory_mb),
                      opacity: 0.85,
                    }}
                  />
                );
              })}
            </div>
            <div className="flex justify-between mt-2 text-xs text-muted-foreground">
              <span>{history[0]?.time}</span>
              <span>🟢 &lt;200MB · 🟡 200–400MB · 🔴 &gt;400MB</span>
              <span>{history[history.length - 1]?.time}</span>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Terminal className="w-4 h-4" />
            {t('admin_server_recent_logs', 'Recent Logs')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="bg-slate-900 rounded-lg p-4 max-h-80 overflow-y-auto font-mono text-xs space-y-0.5">
            {logs.error.length === 0 && logs.out.length === 0 ? (
              <span className="text-slate-500">{t('admin_server_no_logs', 'No logs available')}</span>
            ) : (
              <>
                {logs.out.map((l, i) => logLine(l, `out-${i}`))}
                {logs.error.length > 0 && (
                  <div className="pt-2 mt-2 border-t border-slate-700">
                    <Badge variant="destructive" className="mb-1">stderr</Badge>
                    {logs.error.map((l, i) => logLine(l, `err-${i}`))}
                  </div>
                )}
              </>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ServerMonitor;
